import * as React from 'react'
import { classNames } from '~/utilities'

interface HTMLElementWithClassName<T extends HTMLElement>
  extends React.HTMLAttributes<T> {
  className?: string
  colSpan?: number
}

const Table = React.forwardRef<
  HTMLTableElement,
  HTMLElementWithClassName<HTMLTableElement> & {
    asChild?: boolean
    containerClassName?: string
  }
>(({ className, containerClassName, asChild, ...props }, ref) => {
  if (asChild) {
    return (
      <table
        ref={ref}
        className={classNames('w-full caption-bottom text-sm', className)}
        {...props}
      />
    )
  }
  return (
    <div className={classNames('relative w-full overflow-auto', containerClassName)}>
      <table
        ref={ref}
        className={classNames('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  )
})
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  HTMLElementWithClassName<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={classNames('[&_tr]:border-b', className)} {...props} />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  HTMLElementWithClassName<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={classNames('[&_tr:last-child]:border-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  HTMLElementWithClassName<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={classNames(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  HTMLElementWithClassName<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={classNames(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  HTMLElementWithClassName<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={classNames(
      'py-2 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  HTMLElementWithClassName<HTMLTableCellElement>
>(({ className, colSpan, ...props }, ref) => (
  <td
    ref={ref}
    colSpan={colSpan}
    className={classNames('p-2 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  HTMLElementWithClassName<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={classNames('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
}
